// biome-ignore-all lint/suspicious/noRedeclare: TODO - address later
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Clock,
  ExternalLink,
  Info,
  Loader2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ConnectionStatusCard } from "@/components/fivetran/connection-status-card";
import type { FivetranConnection } from "@/components/fivetran/fivetran";
import {
  SERVICE_DESCRIPTIONS,
  SERVICE_LABELS,
} from "@/components/fivetran/fivetran";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

interface FivetranConnectionProps {
  service: "salesforce" | "hubspot" | "marketo" | "gongio";
}

interface ConnectCardResponse {
  connection: FivetranConnection;
  status: "setup_required";
  redirect_uri: string;
}

export function FivetranConnection({ service }: FivetranConnectionProps) {
  const [searchParams] = useSearchParams();
  const [isConnecting, setIsConnecting] = useState(false);

  // Check for status from URL params (callback from Connect Card)
  const urlStatus = searchParams.get("status");

  // Fetch existing connections to see if this service is already set up
  const {
    data: connections,
    isLoading: connectionsLoading,
    refetch,
  } = useQuery({
    queryKey: ["fivetran-connections"],
    queryFn: async (): Promise<FivetranConnection[]> => {
      return api.get<FivetranConnection[]>("fivetran/connections/");
    },
  });

  // Find existing connection for this service
  const existingConnection = connections?.find(
    (conn) => conn.service_name === service,
  );

  // Refetch connections when returning from successful callback
  useEffect(() => {
    if (urlStatus === "success") {
      refetch();
    }
  }, [urlStatus, refetch]);

  const createConnectionMutation = useMutation({
    mutationFn: async (): Promise<ConnectCardResponse> => {
      return api.post<ConnectCardResponse>(`/fivetran/connections/${service}/`);
    },
    onSuccess: (data) => {
      if (data.redirect_uri) {
        setIsConnecting(true);
        // Redirect to Connect Card
        window.location.href = data.redirect_uri;
      }
    },
    onError: (error) => {
      console.error("Failed to create connection:", error);
      setIsConnecting(false);
    },
  });

  const handleConnect = () => {
    createConnectionMutation.mutate();
  };

  const isConnected = existingConnection?.setup_state === "connected";
  const needsSetup = existingConnection?.setup_state === "incomplete";

  return (
    <div className="container mx-auto py-8 max-w-2xl">
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Link to="/settings/connections">
            <Button variant="outline" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Connections
            </Button>
          </Link>
        </div>
        <h1 className="text-3xl font-bold tracking-tight font-heading">
          {SERVICE_LABELS[service]} Integration
        </h1>
        <p className="text-muted-foreground mt-2">
          Set up your {SERVICE_LABELS[service]} data connection
        </p>
      </div>

      {/* Success message from callback */}
      {urlStatus === "success" && (
        <Alert className="mb-6 border-success/20 bg-success/10">
          <CheckCircle className="h-4 w-4 text-success" />
          <AlertDescription className="text-success">
            Connection established successfully! Your data will begin syncing
            shortly.
          </AlertDescription>
        </Alert>
      )}

      {/* Error message from callback */}
      {urlStatus === "error" && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            There was an error setting up your connection. Please try again.
          </AlertDescription>
        </Alert>
      )}

      {/* Current Connection Status */}
      {connectionsLoading ? (
        <Card className="mb-6">
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-full" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-32" />
          </CardContent>
        </Card>
      ) : existingConnection ? (
        <ConnectionStatusCard connection={existingConnection} />
      ) : null}

      {/* Connection setup card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            {isConnected
              ? `Manage ${SERVICE_LABELS[service]}`
              : `Connect ${SERVICE_LABELS[service]}`}
          </CardTitle>
          <CardDescription>{SERVICE_DESCRIPTIONS[service]}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {isConnected ? (
              <Alert className="border-primary/20 bg-primary/10">
                <Info className="h-4 w-4 text-primary" />
                <AlertDescription className="text-primary">
                  Your {SERVICE_LABELS[service]} connection is active and
                  syncing data. You can reconnect to update credentials or
                  change settings.
                </AlertDescription>
              </Alert>
            ) : needsSetup ? (
              <Alert className="border-warning/20 bg-warning/10">
                <Clock className="h-4 w-4 text-warning" />
                <AlertDescription className="text-warning">
                  Your {SERVICE_LABELS[service]} connection needs to be
                  completed. Click below to finish the setup process.
                </AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground">
                Click the button below to securely connect your{" "}
                {SERVICE_LABELS[service]} account. You'll be redirected to{" "}
                {SERVICE_LABELS[service]} to authorize the connection.
              </p>
            )}

            {createConnectionMutation.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Failed to create connection. Please try again.
                </AlertDescription>
              </Alert>
            )}

            <Button
              onClick={handleConnect}
              disabled={createConnectionMutation.isPending || isConnecting}
              className="w-full"
              size="lg"
              variant={isConnected ? "outline" : "default"}
            >
              {createConnectionMutation.isPending || isConnecting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  {isConnecting ? "Redirecting..." : "Creating Connection..."}
                </>
              ) : (
                <>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  {isConnected
                    ? `Reconnect ${SERVICE_LABELS[service]}`
                    : needsSetup
                      ? `Complete ${SERVICE_LABELS[service]} Setup`
                      : `Connect ${SERVICE_LABELS[service]}`}
                </>
              )}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              This will open a secure connection window where you can safely
              provide your credentials.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
